import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { formatFunnelPick, type FunnelPick, type FunnelStepId, type GridKit, type GridPrescription } from '@joker/domain'
import type { CohortFunnel as Funnel } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { ErrorNote, Loading, Panel } from '../ui'

/**
 * A cohort's funnel: how many reached each step, how many are at it now, and
 * how many of those are stuck (waiting longer than normal) or blocked (waiting
 * on us). Kit and Prescription run side by side after purchase; the grid shows
 * where people stand on both at once. Pressing any number opens the Users list
 * filtered to exactly those people, where they can be sorted, tagged and
 * exported; who matches is decided on the server by the tested funnel rules.
 */
export function CohortFunnel({ id, onOpenList }: { id: string; onOpenList: (pick: string) => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.analytics.cohortFunnel.queryOptions({ bucketId: id }))
  const setPick = (p: FunnelPick) => onOpenList(formatFunnelPick(p))

  if (q.error) return <Panel title="Funnel"><ErrorNote error={q.error} /></Panel>
  if (!q.data) return <Panel title="Funnel"><Loading what="the funnel" /></Panel>
  const f = q.data
  const step = (sid: string) => f.steps.find((s) => s.id === sid)!
  const max = Math.max(1, f.inFunnel)

  /* A plain call, not an inline component: an inline one remounts every render and forgets it was open. */
  const stepEl = (sid: string) => <Step key={sid} s={step(sid)} max={max} onPick={setPick} />

  const cell = (kit: string, rx: string) => f.grid.cells.find((c) => c.kit === kit && c.prescription === rx)?.count ?? 0

  return (
    <Panel title="Funnel" note={`${f.inFunnel} in the funnel · press a number to see who`}>
      {stepEl('purchased')}
      <div className="fn-tracks">
        <div>
          <h4 className="fn-track">Kit</h4>
          {stepEl('shipped')}
          {stepEl('delivered')}
        </div>
        <div>
          <h4 className="fn-track">Prescription</h4>
          {stepEl('asked')}
          {stepEl('sent')}
          {stepEl('read')}
          {stepEl('eligible')}
        </div>
      </div>

      <h4 className="fn-track">Kit and prescription together</h4>
      <div className="scroll-x">
        <table className="data fn-grid">
          <thead>
            <tr>
              <th>Kit ↓ · Prescription →</th>
              {f.grid.prescription.map((p) => (
                <th key={p.id} className="r">{p.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {f.grid.kit.map((k) => (
              <tr key={k.id} style={{ cursor: 'default' }}>
                <td>{k.label}</td>
                {f.grid.prescription.map((p) => {
                  const n = cell(k.id, p.id)
                  const ready = k.id === 'delivered' && p.id === 'eligible'
                  return (
                    <td key={p.id} className={`r num${ready ? ' fn-ready' : ''}`}>
                      {n > 0 ? (
                        <button
                          type="button"
                          className="linklike"
                          onClick={() => setPick({ kind: 'cell', kit: k.id as GridKit, prescription: p.id as GridPrescription })}
                        >
                          {n}
                        </button>
                      ) : (
                        <span className="muted">0</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="footnote">Bottom right, delivered and eligible: ready to activate.</p>

      <h4 className="fn-track">Training</h4>
      {stepEl('activated')}
      {stepEl('started')}
      {stepEl('onboarded')}

      <p className="fn-exits">
        Left the funnel:{' '}
        <button type="button" className="linklike" onClick={() => setPick({ kind: 'exit', exit: 'refunded' })}>
          refunded {f.exits.refunded}
        </button>{' '}
        ·{' '}
        <button type="button" className="linklike" onClick={() => setPick({ kind: 'exit', exit: 'not_eligible' })}>
          not eligible {f.exits.notEligible}
        </button>
        {f.exits.refundFailed > 0 && <span className="fn-tag bad"> {f.exits.refundFailed} refund failed</span>}
      </p>
    </Panel>
  )
}

/* Its own component, so a step stays open when the list below it changes. */
function Step({ s, max, onPick }: { s: Funnel['steps'][number]; max: number; onPick: (p: FunnelPick) => void }) {
  /* Quiet by default: the bar, and a small "▸" that opens the states inside
     the step as a plain list. Each state opens its people. */
  const [open, setOpen] = useState(false)
  return (
    <div className="fn-step">
      <button type="button" className="fn-head fn-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="fn-label">
          <span className="fn-caret" aria-hidden="true">{open ? '▾' : '▸'}</span> {s.label}
        </span>
        <span className="fn-bar" aria-hidden="true">
          <span style={{ width: `${(s.reached / max) * 100}%` }} />
        </span>
        <span className="fn-num num">{s.reached}</span>
      </button>
      <div className="fn-now">
        {s.here > 0 ? (
          <button type="button" className="linklike" onClick={() => onPick({ kind: 'here', step: s.id as FunnelStepId })}>
            {s.here} here now
          </button>
        ) : (
          <span className="muted">none here now</span>
        )}
        {s.stuck > 0 && <span className="fn-tag warn"> {s.stuck} stuck</span>}
        {s.blocked > 0 && <span className="fn-tag bad"> {s.blocked} waiting on us</span>}
      </div>
      {open && (
        <ul className="fn-statuses">
          {s.statuses.map((st) => (
            <li key={st.status}>
              <button
                type="button"
                className="linklike"
                onClick={() => onPick({ kind: 'status', step: s.id as FunnelStepId, status: st.status })}
              >
                {st.label}
              </button>{' '}
              <span className="num">{st.count}</span>
              {st.stuck > 0 && <span className="fn-tag warn"> {st.stuck} stuck</span>}
              {st.blocked > 0 && <span className="fn-tag bad"> waiting on us</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

